import { Component, OnInit, OnDestroy, ChangeDetectorRef, ApplicationRef } from '@angular/core';
import { HistoryService, PageableAudit } from '../../services/history.service';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ParseJsonPipe } from './../../pipes/parse-json.pipe';
import { TableType } from '../../types/table-types.type';
import { LogType } from '../../types/log-types.type';
import { UserService } from '../../services/user.service';
import { Subject, takeUntil, finalize } from 'rxjs';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PaginatorModule } from 'primeng/paginator';

@Component({
  selector: 'app-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ParseJsonPipe,
    CardModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    TableModule,
    ProgressSpinnerModule,
    PaginatorModule
  ],
  template: `
    <p-card>
      <ng-template pTemplate="header">
        <div class="card-header">
          <h2 class="card-title"><i class="pi pi-history"></i> Histórico de Ações</h2>
        </div>
      </ng-template>

      <div *ngIf="isShowing" class="spinner-overlay">
        <p-progressSpinner styleClass="custom-spinner" strokeWidth="4"></p-progressSpinner>
        <p class="loading-text">Carregando dados...</p>
      </div>

      <div class="admin-logs-container">
        <form [formGroup]="filterForm" (ngSubmit)="searchLogs()">
          <div class="filter-inputs">
            <div class="form-field">
              <label class="field-label">Data Inicial</label>
              <p-calendar
                formControlName="initialDate"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                placeholder="Selecione a data"
                styleClass="w-full">
              </p-calendar>
            </div>

            <div class="form-field">
              <label class="field-label">Data Final</label>
              <p-calendar
                formControlName="finalDate"
                dateFormat="dd/mm/yy"
                [showIcon]="true"
                placeholder="Selecione a data"
                styleClass="w-full">
              </p-calendar>
            </div>

            <div class="form-field">
              <label class="field-label">Usuário</label>
              <p-dropdown
                formControlName="userId"
                [options]="users"
                optionLabel="shortName"
                optionValue="id"
                placeholder="Selecione o usuário"
                [filter]="true"
                filterBy="shortName"
                styleClass="w-full"
                [showClear]="true">
              </p-dropdown>
            </div>

            <div class="form-field">
              <label class="field-label">Tabela</label>
              <p-dropdown
                formControlName="table"
                [options]="tableOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Selecione a tabela"
                styleClass="w-full"
                [showClear]="true">
              </p-dropdown>
            </div>

            <div class="form-field">
              <label class="field-label">Ação</label>
              <p-dropdown
                formControlName="action"
                [options]="actionOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Selecione a ação"
                styleClass="w-full"
                [showClear]="true">
              </p-dropdown>
            </div>

            <div class="form-field button-field">
              <p-button
                label="Buscar"
                icon="pi pi-search"
                type="submit"
                styleClass="search-button"
                [style]="{'width': '100%'}">
              </p-button>
            </div>

            <div class="form-field button-field">
              <p-button
                label="Limpar"
                icon="pi pi-times"
                type="button"
                styleClass="clear-button"
                (onClick)="clearFilters()"
                [style]="{'width': '100%'}">
              </p-button>
            </div>
          </div>
        </form>

        <div *ngIf="logData" class="total-records">
          Total de registros encontrados: {{ logData.totalElements }}
        </div>

        <div class="results-container" *ngIf="logData && logData.content.length > 0">
          <p-table
            [value]="logData.content"
            [tableStyle]="{'min-width': '50rem'}"
            styleClass="p-datatable-sm p-datatable-striped">
            <ng-template pTemplate="header">
              <tr>
                <th>ID</th>
                <th>Usuário</th>
                <th>Registro Afetado</th>
                <th>Tabela</th>
                <th>Ação</th>
                <th>Data</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-log>
              <tr>
                <td>{{ log.auditId }}</td>
                <td>{{ log.userShortName }}</td>
                <td>{{ log.afterAction | parseJson | json }}</td>
                <td>{{ log.tableName }}</td>
                <td>{{ log.logType }}</td>
                <td>{{ formatCreatedAt(log.createdAt) }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="6" class="empty-message">
                  Nenhum registro encontrado
                </td>
              </tr>
            </ng-template>
          </p-table>

          <p-paginator
            *ngIf="logData"
            [rows]="logData.size"
            [totalRecords]="logData.totalElements"
            [first]="logData.number * logData.size"
            (onPageChange)="onPageChange($event)"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Página {currentPage} de {totalPages}">
          </p-paginator>
        </div>
      </div>
    </p-card>
  `,
  styles: [
    `
    .card-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 1rem 1rem 0.25rem 1rem;
    }

    .card-header h2 {
      margin: 0;
      color: #325565;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.5rem;
      font-weight: 600;
    }

    .admin-logs-container {
      padding: 0.5rem 1rem 1rem 1rem;
    }

    .filter-inputs {
      display: grid;
      grid-template-columns: repeat(5, 1fr) auto auto;
      gap: 1rem;
      margin-bottom: 2rem;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .field-label {
      color: #325565;
      font-weight: 500;
      font-size: 0.875rem;
      min-height: 1.25rem;
    }

    .button-field {
      display: flex;
      align-items: flex-end;
      padding-top: 1.5rem;
    }

    ::ng-deep .search-button {
      background-color: rgb(16, 148, 158) !important;
      border-color: rgb(16, 148, 158) !important;
      color: white !important;
      height: 42px !important;
    }

    ::ng-deep .search-button:hover {
      background-color: rgb(6, 182, 212) !important;
      border-color: rgb(6, 182, 212) !important;
    }

    ::ng-deep .clear-button {
      background-color: #6b7280 !important;
      border-color: #6b7280 !important;
      color: white !important;
      height: 42px !important;
    }

    ::ng-deep .clear-button:hover {
      background-color: #4b5563 !important;
      border-color: #4b5563 !important;
    }

    ::ng-deep .w-full {
      width: 100%;
    }

    ::ng-deep .p-calendar,
    ::ng-deep .p-dropdown {
      height: 42px;
    }

    ::ng-deep .p-calendar .p-inputtext,
    ::ng-deep .p-dropdown .p-dropdown-label {
      height: 42px;
      display: flex;
      align-items: center;
    }

    ::ng-deep .p-calendar .p-datepicker-trigger {
      height: 42px;
    }

    .total-records {
      margin-bottom: 1rem;
      color: rgb(16, 148, 158);
      font-weight: 600;
      font-size: 0.875rem;
    }

    .results-container {
      margin-top: 2rem;
      overflow-x: auto;
    }

    ::ng-deep .p-datatable {
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
    }

    ::ng-deep .p-datatable .p-datatable-thead > tr > th {
      background-color: transparent;
      color: #325565;
      font-weight: 600;
      padding: 1rem;
      text-align: left;
      border-bottom: 2px solid #e5e7eb;
    }

    ::ng-deep .p-datatable .p-datatable-tbody > tr > td {
      padding: 0.75rem 1rem;
      color: #374151;
    }

    ::ng-deep .p-datatable .p-datatable-tbody > tr:hover {
      background-color: #f9fafb;
    }

    .empty-message {
      text-align: center;
      color: #6b7280;
      padding: 2rem !important;
      font-style: italic;
    }

    ::ng-deep .p-paginator {
      margin-top: 1rem;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      background: white;
    }

    ::ng-deep .p-paginator .p-paginator-current {
      color: #325565;
      font-weight: 500;
    }

    .spinner-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 1000;
      gap: 1rem;
    }

    .loading-text {
      color: white;
      font-size: 1.125rem;
      font-weight: 500;
      margin: 0;
    }

    ::ng-deep .custom-spinner {
      width: 60px;
      height: 60px;
    }

    ::ng-deep .custom-spinner .p-progress-spinner-circle {
      stroke: #F15A22;
    }

    @media (max-width: 768px) {
      .filter-inputs {
        grid-template-columns: 1fr;
      }

      .results-container {
        overflow-x: scroll;
      }
    }
    `
  ]
})
export class HistoryComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();
  logData: PageableAudit | undefined;
  currentPage = 0;
  users: { id: number; shortName: string }[] = [];
  filterForm: FormGroup;

  isShowing: boolean = false;

  tableOptions = [
    { value: '', label: '' },
    ...Object.keys(TableType).map(key => ({
      value: key,
      label: TableType[key as keyof typeof TableType]
    })),
  ];

  actionOptions = [
    { value: '', label: '' },
    ...Object.keys(LogType).map(key => ({
      value: key,
      label: LogType[key as keyof typeof LogType]
    })),
  ];

  constructor(
    private historyService: HistoryService,
    private userService: UserService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private appRef: ApplicationRef
  ) {
    this.filterForm = this.fb.group({
      initialDate: [''],
      finalDate: [''],
      userId: [''],
      table: [''],
      action: ['']
    });
  }

  ngOnInit() {
    this.userService.getUsers()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (response?.data) {
            this.users = response.data.sort((a, b) => a.shortName.localeCompare(b.shortName));
          }
        },
        error: (error) => console.error('Erro ao carregar usuários', error)
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  searchLogs() {
    if (this.filterForm.invalid) return;

    this.isShowing = true;
    this.logData = undefined;
    const formValue = this.filterForm.value;
    
    // Converter datas para formato esperado pela API
    const initialDate = formValue.initialDate ? this.formatDate(formValue.initialDate) : '';
    const finalDate = formValue.finalDate ? this.formatDate(formValue.finalDate) : '';

    this.historyService.getAuditByPeriod(
      initialDate,
      finalDate,
      formValue.userId ? Number(formValue.userId) : undefined,
      formValue.table,
      formValue.action,
      this.currentPage
    ).pipe(
      takeUntil(this.destroyed$),
      finalize(() => {
        this.isShowing = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (data: PageableAudit) => {
        this.logData = data;
        this.cdr.markForCheck();
        // setTimeout + tick global garante que PrimeNG renderize todas as rows
        setTimeout(() => {
          this.appRef.tick();
          this.cdr.detectChanges();
        }, 0);
      },
      error: (error: any) => {
        console.error('Erro na busca', error);
      }
    });
  }

  private formatDate(date: Date): string {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  onPageChange(event: any) {
    this.currentPage = event.page ?? (event.first / (this.logData?.size || 10));
    this.searchLogs();
  }

  changePage(direction: number) {
    if (this.logData) {
      const newPage = this.logData.number + direction;
      if (newPage >= 0 && newPage < this.logData.totalPages) {
        this.currentPage = newPage;
        this.searchLogs();
      }
    }
  }

  clearFilters() {
    this.filterForm.reset();
    this.currentPage = 0;
    this.logData = undefined;
  }

  /**
   * Converte createdAt que pode vir como:
   * - ISO 8601 string: "2026-06-11T07:15:41"
   * - Java LocalDateTime.toString(): "2026,6,11,7,15,41,720124000"
   * - Array de números: [2026, 6, 11, 7, 15, 41, 720124000]
   * - Timestamp numérico (epoch ms)
   */
  formatCreatedAt(value: any): string {
    if (value == null) return '';

    // Caso 1: array [2026, 6, 11, 7, 15, 41, ...]
    if (Array.isArray(value) && value.length >= 3) {
      const [year, month, day, hour = 0, minute = 0] = value;
      return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    }

    // Caso 2: número (epoch ms)
    if (typeof value === 'number') {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return this.toLocalFormat(d);
      return String(value);
    }

    // Caso 3: string
    if (typeof value === 'string') {
      // ISO 8601
      const isoDate = new Date(value);
      if (!isNaN(isoDate.getTime())) return this.toLocalFormat(isoDate);

      // Java LocalDateTime.toString()
      const parts = value.split(',');
      if (parts.length >= 6) {
        const [year, month, day, hour, minute] = parts.map(Number);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        }
      }

      return value;
    }

    return String(value);
  }

  private toLocalFormat(date: Date): string {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }
}
